import {useEffect, useRef, useState} from "react";
import {DOB_RESOLVED_EVENT, readStoredDob} from "../../myCase/dobSession";

// When true, skip persisting to sessionStorage (sign-out clears the cache;
// Home/Dashboard unmount must not write it back a tick later).
let suppressProgressPersist = false;

export const progressStorageKey = (customerId, dateOfBirth) => {
  if (!customerId) return null;
  return dateOfBirth ? `vp_${customerId}:${dateOfBirth}` : `vp_${customerId}`;
};

const emptyProgress = () => ({activity: {}, watchHistory: {}});

// Per-user activity + watch_history cache in this tab. Must be cleared on
// sign-out: otherwise a wiped DynamoDB row (or a different DOB-matched
// participant on a shared login) still looks "orientation complete" from
// leftover local progress.
export const clearVideoProgressCache = (customerId) => {
  suppressProgressPersist = true;
  try {
    if (customerId) {
      sessionStorage.removeItem(`vp_${customerId}`);
    }
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith("vp_")) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // Private mode / quota — ignore.
  }
};

export const useStorage = ({user}) => {
  const [activity, _setActivity] = useState({});
  const [watchHistory, _setWatchHistory] = useState({});
  const [dobTick, setDobTick] = useState(0);

  const storageTimerRef = useRef(null);
  const latestDataRef = useRef(emptyProgress());

  const storedDob = user?.customer_id ? readStoredDob(user.customer_id) : null;
  const storageKey = progressStorageKey(user?.customer_id, storedDob);

  useEffect(() => {
    const customerId = user?.customer_id;
    if (!customerId) return undefined;
    const onResolved = (e) => {
      if (e?.detail?.customerId && e.detail.customerId !== customerId) return;
      // Drop the unscoped login-level cache before the key switches to
      // include DOB, and forget in-memory progress from the previous person.
      try {
        sessionStorage.removeItem(`vp_${customerId}`);
      } catch {
        // ignore
      }
      const empty = emptyProgress();
      latestDataRef.current = empty;
      _setActivity(empty.activity);
      _setWatchHistory(empty.watchHistory);
      setDobTick((n) => n + 1);
    };
    window.addEventListener(DOB_RESOLVED_EVENT, onResolved);
    return () => window.removeEventListener(DOB_RESOLVED_EVENT, onResolved);
  }, [user?.customer_id]);

  useEffect(() => {
    // New signed-in session may persist again.
    if (user?.customer_id) suppressProgressPersist = false;
  }, [user?.customer_id]);

  useEffect(() => {
    latestDataRef.current = {
      activity,
      watchHistory,
    };
  }, [activity, watchHistory]);

  const scheduleStorageSave = (nextData) => {
    if (!storageKey || suppressProgressPersist) return;

    latestDataRef.current = {
      ...latestDataRef.current,
      ...nextData,
    };

    if (storageTimerRef.current) {
      clearTimeout(storageTimerRef.current);
    }

    storageTimerRef.current = setTimeout(() => {
      if (suppressProgressPersist) return;
      sessionStorage.setItem(storageKey, JSON.stringify(latestDataRef.current));
    }, 1000);
  };

  useEffect(() => {
    if (!user?.customer_id) return;

    const cached = storageKey ? sessionStorage.getItem(storageKey) : null;

    if (!cached) {
      const empty = emptyProgress();
      _setActivity(empty.activity);
      _setWatchHistory(empty.watchHistory);
      latestDataRef.current = empty;
      return;
    }

    try {
      const {activity: act, watchHistory: wh} = JSON.parse(cached);
      const nextActivity = act && typeof act === "object" ? act : {};
      const nextHistory = wh && typeof wh === "object" ? wh : {};
      _setActivity(nextActivity);
      _setWatchHistory(nextHistory);
      latestDataRef.current = {
        activity: nextActivity,
        watchHistory: nextHistory,
      };
    } catch (e) {
      console.error(e);
      const empty = emptyProgress();
      _setActivity(empty.activity);
      _setWatchHistory(empty.watchHistory);
      latestDataRef.current = empty;
    }
  }, [user?.customer_id, storageKey, dobTick]);

  useEffect(() => {
    return () => {
      if (storageTimerRef.current) {
        clearTimeout(storageTimerRef.current);
      }

      // Do not re-seed the cache after sign-out cleared it.
      if (storageKey && !suppressProgressPersist) {
        sessionStorage.setItem(
          storageKey,
          JSON.stringify(latestDataRef.current),
        );
      }
    };
  }, [storageKey]);

  const setActivity = (activity) => {
    if (!storageKey) return;

    if (typeof activity === "function") {
      _setActivity((prev) => {
        const nextActivity = activity(prev);
        scheduleStorageSave({activity: nextActivity});
        return nextActivity;
      });
    } else if (typeof activity === "object") {
      _setActivity(activity);
      scheduleStorageSave({activity});
    }
  };

  const setWatchHistory = (watchHistory) => {
    if (!storageKey) return;

    if (typeof watchHistory === "function") {
      _setWatchHistory((prev) => {
        const nextWatchHistory = watchHistory(prev);
        scheduleStorageSave({watchHistory: nextWatchHistory});
        return nextWatchHistory;
      });
    } else if (typeof watchHistory === "object") {
      _setWatchHistory(watchHistory);
      scheduleStorageSave({watchHistory});
    }
  };

  return {
    activity,
    watchHistory,
    setActivity,
    setWatchHistory,
  };
};
